import React, { useEffect, useState } from 'react';

import { ChevronDown, Tag } from 'lucide-react';

import { BracketSeedLogo } from '../common/BracketSeedLogo';
import ProfileMenu from './ProfileMenu';
import packageJson from '../../../package.json'; // Adjust the path to your `package.json`
import { API_BASE_URL } from '../../config/config';
import { UserClient } from '../../services/apiClient';
import axiosInstance from '../../services/axiosInstance';
import {
  getUserName,
  getAdminUserName,
  getUserId,
} from '../../utils/tokenUtils';

const Topbar: React.FC = () => {
  const userId = getUserId();
  const userName = getUserName() || '';
  const adminUsername = getAdminUserName();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(
    () => {
      // Get stored URL immediately on mount
      const storedData = localStorage.getItem(`profilePic_${userId}`);
      if (storedData) {
        const { url, expiresAt } = JSON.parse(storedData);

        return expiresAt > Date.now() ? url : null; // Only use if not expired
      }

      return null;
    },
  );
  const userClient = new UserClient(API_BASE_URL, axiosInstance);

  const toggleProfileMenu = (): void => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  // Function to get stored SAS URL from localStorage
  const getStoredSasUrl = (): {
    url: string | null;
    expiresAt: number | null;
  } => {
    const storedData = localStorage.getItem(`profilePic_${userId}`);
    if (!storedData) {
      return { url: null, expiresAt: null };
    }

    const { url, expiresAt } = JSON.parse(storedData);

    return { url, expiresAt };
  };

  // Function to save SAS URL in localStorage
  const storeSasUrl = (url: string, expiresInMinutes: number): void => {
    const expiresAt = Date.now() + (expiresInMinutes - 1) * 60 * 1000; // Expire 1 min before the actual expiry
    localStorage.setItem(
      `profilePic_${userId}`,
      JSON.stringify({ url, expiresAt }),
    );
  };

  // Fetch profile picture SAS URL
  useEffect(() => {
    if (!userId) {
      return;
    }

    const { url: storedUrl, expiresAt } = getStoredSasUrl();

    // If stored SAS URL is valid, use it immediately
    if (storedUrl && expiresAt && expiresAt > Date.now()) {
      setProfilePictureUrl(storedUrl);
    }

    const fetchProfilePicture = async (): Promise<void> => {
      try {
        const profilePicResponse =
          await userClient.generateProfilePictureSas(userId);
        if (profilePicResponse.success) {
          const newUrl = profilePicResponse.data ?? '';
          setProfilePictureUrl(newUrl);
          storeSasUrl(newUrl, 5);
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };

    // Only fetch a new SAS token if it's expired or about to expire
    if (!storedUrl || !expiresAt || expiresAt < Date.now()) {
      fetchProfilePicture();
    }
  }, [userId]);

  const getInitials = (name: string): string => {
    const initials = name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();

    return initials || 'U';
  };

  return (
    <header className="flex items-center justify-between h-16 min-h-16 px-5 bg-panel border-b border-line">
      <div className="flex items-center space-x-4">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <BracketSeedLogo variant="dark" size={42} />
        </a>

        {/* Version */}
        <div className="hidden lg:flex items-center gap-1.5 bg-panel2 border border-[rgba(255,255,255,0.13)] rounded-full px-2.5 py-1">
          <Tag className="w-3 h-3 text-muted" />
          <span className="font-['Space_Mono'] text-[10px] text-muted">v{packageJson.version}</span>
        </div>
      </div>

      {/* Right side controls */}
      <div className="flex items-center space-x-4">
        {/* Profile Dropdown */}
        <div className="relative">
          <button
            className="flex items-center space-x-2 p-2 hover:bg-[rgba(255,255,255,0.04)] transition-colors cursor-pointer border-none bg-transparent"
            onClick={toggleProfileMenu}
          >
            {/* Profile Picture or Initials */}
            {profilePictureUrl ? (
              <img
                className="w-9 h-9 rounded-full"
                src={profilePictureUrl}
                alt="Profile"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[rgba(146,222,113,0.14)] flex items-center justify-center font-['Archivo'] text-[11px] font-bold text-accent">
                {getInitials(userName)}
              </div>
            )}
            <div className="flex flex-col items-start">
              <span className="font-['Archivo'] text-[13px] text-text hidden sm:block">
                {userName}
              </span>
              {adminUsername && (
                <span className="font-['Space_Mono'] text-[9px] text-muted hidden sm:block">
                  {adminUsername}
                </span>
              )}
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-muted" />
          </button>

          {profileMenuOpen && <ProfileMenu />}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
